import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myListings, setMyListings] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [received, setReceived] = useState([]);
  const [threads, setThreads] = useState([]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/my/listings").then((r) => r.data).catch(() => []),
      api.get("/my/applications").then((r) => r.data).catch(() => []),
      api.get("/my/received-applications").then((r) => r.data).catch(() => []),
      api.get("/messages/threads").then((r) => r.data).catch(() => []),
    ]).then(([l, a, r, t]) => {
      setMyListings(l);
      setMyApps(a);
      setReceived(r);
      setThreads(t);
    });
  }, [user]);

  const tierLabel = (t) => ({
    free: "Free · the wanderer's purse",
    starter: "Starter · a short residence",
    standard: "Standard · the settled hermit",
    annual: "Annual · the lifelong recluse",
  }[t] || "Free");

  return (
    <main className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
        <div>
          <p className="label-overline">Your hermitage</p>
          <h1 className="font-display text-5xl sm:text-6xl mt-2">
            Welcome, <em className="text-wax-red">{user?.name || "Friend"}</em>.
          </h1>
          <p className="font-body text-ink-iron mt-3">
            {tierLabel(user?.subscription_tier)} · {user?.role === "host" ? "Host" : user?.role === "hermit" ? "Hermit" : "Either path"}
          </p>
          {user?.founding_member && (
            <p className="label-overline mt-3 text-wax-red" data-testid="founding-badge">
              ❦ Founding Member · {user?.founding_cohort === "host" ? "Host cohort" : user?.founding_cohort === "hermit" ? "Hermit cohort" : "Comp"}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/listings/new" data-testid="dashboard-new-listing" className="wax-button">Post a listing</Link>
          <Link to="/profile" data-testid="dashboard-edit-profile" className="ghost-button">Edit profile</Link>
          <Link to="/email-preferences" data-testid="dashboard-email-prefs" className="ghost-button">Email preferences</Link>
          {user?.is_admin && (
            <Link to="/admin/scheduler" data-testid="dashboard-admin-scheduler" className="ghost-button border-wax-red text-wax-red">Scheduler</Link>
          )}
          {user?.subscription_tier === "free" ? (
            <Link to="/pricing" data-testid="dashboard-upgrade" className="ghost-button">Upgrade</Link>
          ) : (
            <button
              data-testid="dashboard-manage-subscription"
              onClick={async () => {
                try {
                  const { data } = await api.post("/subscription/portal", { return_url: window.location.href });
                  window.location.href = data.url;
                } catch (e) {
                  alert("Could not open subscription portal: " + (e.response?.data?.detail || e.message));
                }
              }}
              className="ghost-button"
            >Manage subscription</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My listings */}
        <section className="parchment-card lg:col-span-2" data-testid="dashboard-my-listings">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl">My listings</h2>
            <Link to="/listings/new" className="label-overline text-wax-red hover:underline">+ New</Link>
          </div>
          {myListings.length === 0 ? (
            <p className="marginalia">No listings yet. Post a hermitage you offer, or a hermit-letter describing yourself.</p>
          ) : (
            <ul className="divide-y divide-rule">
              {myListings.map((l) => (
                <li key={l.listing_id} className="py-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="label-overline">{l.listing_type === "hermitage" ? "Hermitage" : "Hermit offer"} · {l.region}</p>
                    <Link to={`/listings/${l.listing_id}`} className="font-display text-xl hover:text-wax-red">{l.title}</Link>
                    <p className="font-body text-sm text-ink-iron mt-1">{l.summary}</p>
                  </div>
                  <button
                    data-testid={`delete-listing-${l.listing_id}`}
                    onClick={async () => {
                      if (!window.confirm("Withdraw this listing?")) return;
                      await api.delete(`/listings/${l.listing_id}`);
                      setMyListings(myListings.filter((x) => x.listing_id !== l.listing_id));
                    }}
                    className="label-overline text-ink-marginalia hover:text-wax-red"
                  >Withdraw</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Messages */}
        <section className="parchment-card" data-testid="dashboard-threads">
          <h2 className="font-display text-2xl mb-4">Correspondence</h2>
          {threads.length === 0 ? (
            <p className="marginalia">No letters yet. Send one.</p>
          ) : (
            <ul className="divide-y divide-rule">
              {threads.map((t, i) => (
                <li key={i} className="py-3">
                  <button
                    data-testid={`open-thread-${t.other_user_id}`}
                    onClick={() => navigate(`/messages/${t.other_user_id}`)}
                    className="text-left w-full"
                  >
                    <p className="font-display text-lg">{t.last_message.sender_id === user.id ? t.last_message.recipient_name : t.last_message.sender_name}</p>
                    <p className="font-body text-sm text-ink-iron line-clamp-2">{t.last_message.body}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Applications I sent */}
        <section className="parchment-card" data-testid="dashboard-my-apps">
          <h2 className="font-display text-2xl mb-4">Letters I have sent</h2>
          {myApps.length === 0 ? (
            <p className="marginalia">You have not yet applied to a hermitage.</p>
          ) : (
            <ul className="divide-y divide-rule">
              {myApps.map((a) => (
                <li key={a.application_id} className="py-3">
                  <p className="label-overline">{a.status}</p>
                  <Link to={`/listings/${a.listing_id}`} className="font-display text-lg hover:text-wax-red">{a.listing_title}</Link>
                  <p className="font-body text-sm text-ink-iron line-clamp-2">{a.letter}</p>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Letters received */}
        <section className="parchment-card lg:col-span-2" data-testid="dashboard-received">
          <h2 className="font-display text-2xl mb-4">Letters received</h2>
          {received.length === 0 ? (
            <p className="marginalia">No applicants to your listings yet.</p>
          ) : (
            <ul className="divide-y divide-rule">
              {received.map((a) => (
                <li key={a.application_id} className="py-4">
                  <p className="label-overline">{a.status} · to {a.listing_title}</p>
                  <p className="font-display text-lg">From {a.applicant_name}</p>
                  <p className="font-body text-sm text-ink-iron mt-1 line-clamp-3">{a.letter}</p>
                  <button
                    data-testid={`reply-${a.applicant_id}`}
                    onClick={() => navigate(`/messages/${a.applicant_id}`)}
                    className="label-overline text-wax-red mt-2 hover:underline"
                  >Reply →</button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
